<script>
import BaseForm from '@/model/baseForm/BaseForm/BaseForm.vue'
import LoadingProvider from '@/views/widget/LoadingProvider.vue'
import { getMembershipSystem, updateMembershipSystem } from '@/model/dataLayer/api/MembershipSystem/MembershipSystem'
import { saveFile } from '@/model/utils/utils'
import { colorList } from '@/model/dataLayer/api/Vip/VipCommon'
import EnhancedColorPicker from '@/model/baseForm/fields/EnhancedColorPicker.vue'
import EnhancedImageUploader from '@/model/baseForm/fields/EnhancedImageUploader.vue'
import { createDisplayInfoFormFields } from '@/model/baseForm/membershipBenefitFormSchema'

export default {
  name: 'MembershipSystemForm',
  components: {
    LoadingProvider,
    BaseForm
  },
  props: {
    systemInfo: {
      type: Object,
      default: () => ({})
    },
    textColor: {
      type: String,
      default: 'black'
    }
  },
  data () {
    return {
      editMode: false,
      showInviteDialog: false,
      loading: false,
      btnLoading: false,
      saveLoading: false,
      showEditDialog: false,
      localSystemInfo: {
        displayInfo: {
          name: '',
          description: '',
          imageUrl: '',
          color: '#eeeeee'
        },
        pointRule: {
          pointsPerUnit: 0,
          minimumSpend: 0,
          maxPointsPerTransaction: 0,
          expirationDays: 0
        },
        horizontalLogoUrl: '',
        promotionalImageUrl: '',
        brandSecondaryColor: '#FFFFFF',
        descriptionTextMD: ''
      },
      colorList
    }
  },
  watch: {
    systemInfo: {
      immediate: true,
      deep: true,
      handler (val) {
        if (val && Object.keys(val).length > 0) {
          // Deep merge to ensure all nested properties are preserved
          const deepCopy = JSON.parse(JSON.stringify(val))

          // Ensure displayInfo exists
          if (!deepCopy.displayInfo) {
            deepCopy.displayInfo = this.localSystemInfo.displayInfo
          }

          // Ensure pointRule exists
          if (!deepCopy.pointRule) {
            deepCopy.pointRule = this.localSystemInfo.pointRule
          }

          this.localSystemInfo = deepCopy
        }
      }
    }
  },
  async mounted () {
    if (!this.systemInfo || Object.keys(this.systemInfo).length === 0) {
      await this.reload()
    }
  },
  computed: {
    displayInfo: function () {
      return this.localSystemInfo.displayInfo
    },
    fullSchema () {
      return [
        // Display Info Section
        ...createDisplayInfoFormFields({
          nameHint: '会员系统名称',
          descriptionHint: '会员系统描述',
          colorHint: '会员系统颜色',
          imageHint: '会员系统图片',
          colorRequired: false,
          imageRequired: false,
          componentProps: {
            name: {},
            description: {},
            color: {
              textColorValue: this.textColor
            },
            imageUrl: {
              maxSizeMB: 1,
              hint: '上传会员系统图片 (JPG, PNG)，推荐尺寸：200x200像素，1:1比例'
            }
          }
        }),

        // Image Uploads Section
        {
          key: 'horizontalLogoUrl',
          name: 'horizontalLogoUrl',
          hint: '水平Logo',
          component: EnhancedImageUploader,
          required: false,
          default: '',
          componentProps: {
            maxSizeMB: 1,
            hint: '上传水平Logo (JPG, PNG)，推荐尺寸：300x120像素，5:2比例'
          }
        },

        // Color Selection Section
        {
          key: 'brandSecondaryColor',
          name: 'brandSecondaryColor',
          hint: '品牌辅助色',
          component: EnhancedColorPicker,
          required: false,
          default: '#FFFFFF',
          componentProps: {
            textColorValue: this.textColor
          }
        }
      ]
    }
  },
  methods: {
    async reload () {
      this.loading = true
      const systemData = await getMembershipSystem()

      // Ensure the required nested objects exist
      if (!systemData.displayInfo) {
        systemData.displayInfo = {
          name: '',
          description: '',
          imageUrl: '',
          color: '#eeeeee'
        }
      }

      if (!systemData.pointRule) {
        systemData.pointRule = {
          pointsPerUnit: 0,
          minimumSpend: 0,
          maxPointsPerTransaction: 0,
          expirationDays: 0
        }
      }

      this.localSystemInfo = systemData
      this.$emit('update:system-info', this.localSystemInfo)
      this.loading = false
    },
    async submit (info) {
      this.saveLoading = true
      try {
        // Ensure the required nested objects exist
        if (!info.displayInfo) {
          info.displayInfo = {
            name: '',
            description: '',
            imageUrl: '',
            color: '#eeeeee'
          }
        }

        if (!info.pointRule) {
          info.pointRule = {
            pointsPerUnit: 0,
            minimumSpend: 0,
            maxPointsPerTransaction: 0,
            expirationDays: 0
          }
        }

        // Process image uploads
        if (info.displayInfo?.imageUrl && typeof info.displayInfo.imageUrl !== 'string') {
          info.displayInfo.imageUrl = await saveFile(info.displayInfo.imageUrl)
        }
        if (info.horizontalLogoUrl && typeof info.horizontalLogoUrl !== 'string') {
          info.horizontalLogoUrl = await saveFile(info.horizontalLogoUrl)
        }
        if (info.promotionalImageUrl && typeof info.promotionalImageUrl !== 'string') {
          info.promotionalImageUrl = await saveFile(info.promotionalImageUrl)
        }

        // If we're in standalone mode, save directly
        if (!this.systemInfo || Object.keys(this.systemInfo).length === 0) {
          await updateMembershipSystem(info)
        }

        // Emit the updated info to the parent
        this.$emit('update:system-info', info)
      } catch (error) {
        console.error('Error updating system:', error)
      } finally {
        this.saveLoading = false
      }
    }
  }
}
</script>

<template>
  <div>
    <loading-provider :loading="loading">
      <!-- Unified Membership System Form with fixed height and scrolling -->
      <div class="form-container">
        <base-form
          color="#fefefe"
          :editing-object="localSystemInfo"
          :is-dialog="false"
          :schema="fullSchema"
          :loading="saveLoading"
          class="pa-4"
          @submit="submit"
        >
          <div>会员系统设置</div>
          <template #subtitle>
            在这里修改会员系统的所有设置
          </template>
        </base-form>
      </div>
    </loading-provider>
  </div>
</template>

<style scoped>
.form-container {
  max-height: calc(100vh - 144px); /* Adjust the value as needed to fit your layout */
  overflow-y: auto;
  border-radius: 4px;
}
</style>
